import { useEffect, useRef, useState } from "react";
import { AUDIO_TUH, AUDIO_GAH } from "@/lib/constants";

// Two-button sound feature: "Dat Bih Tuh" and "Dat Bih Gah".
// Each button plays its own mp3. Tap-spam-friendly (resets currentTime).
export const DatBih = () => {
  const tuhRef = useRef(null);
  const gahRef = useRef(null);
  const [playing, setPlaying] = useState(null); // "tuh" | "gah" | null
  const [pulse, setPulse] = useState(null);

  useEffect(() => {
    tuhRef.current = new Audio(AUDIO_TUH);
    gahRef.current = new Audio(AUDIO_GAH);
    tuhRef.current.preload = "auto";
    gahRef.current.preload = "auto";
    const onEndTuh = () => setPlaying((p) => (p === "tuh" ? null : p));
    const onEndGah = () => setPlaying((p) => (p === "gah" ? null : p));
    tuhRef.current.addEventListener("ended", onEndTuh);
    gahRef.current.addEventListener("ended", onEndGah);
    return () => {
      tuhRef.current?.pause();
      gahRef.current?.pause();
      tuhRef.current?.removeEventListener("ended", onEndTuh);
      gahRef.current?.removeEventListener("ended", onEndGah);
    };
  }, []);

  const play = (which) => {
    const a = which === "tuh" ? tuhRef.current : gahRef.current;
    const other = which === "tuh" ? gahRef.current : tuhRef.current;
    if (!a) return;
    try {
      other?.pause();
      if (other) other.currentTime = 0;
      a.currentTime = 0;
      a.play().catch(() => {});
      setPlaying(which);
      setPulse(which);
      setTimeout(() => setPulse(null), 380);
    } catch (e) {
      // ignore
    }
  };

  return (
    <section
      id="soundboard"
      data-testid="soundboard-section"
      className="relative z-10 px-4 sm:px-8 lg:px-12 py-16"
    >
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center gap-3 mb-2">
          <span className="tag">CHAPTER 02</span>
          <span className="font-luckiest text-lg">THE SOUNDBOARD</span>
        </div>
        <h2
          className="font-bangers leading-[0.9] text-black mb-3"
          style={{ fontSize: "clamp(38px, 6vw, 84px)" }}
        >
          Press it.{" "}
          <span style={{ color: "#0057FF", WebkitTextStroke: "2px #000" }}>
            Say it.
          </span>
        </h2>
        <p className="font-luckiest text-base sm:text-lg max-w-2xl text-black/80">
          two buttons. two moods. one whole vibe. tap em.
        </p>

        <div className="mt-10 grid md:grid-cols-2 gap-6 sm:gap-8">
          <SoundButton
            label="DAT BIH TUH"
            sub="press for the lil one"
            color="#FFD500"
            testId="dat-bih-tuh-button"
            onClick={() => play("tuh")}
            active={playing === "tuh"}
            pulsing={pulse === "tuh"}
          />
          <SoundButton
            label="DAT BIH GAH"
            sub="press for the loud one"
            color="#0057FF"
            textColor="#fff"
            testId="dat-bih-gah-button"
            onClick={() => play("gah")}
            active={playing === "gah"}
            pulsing={pulse === "gah"}
          />
        </div>

        <div
          className="mt-8 sticker p-4 sm:p-5 flex items-center justify-between gap-3"
          data-testid="soundboard-status"
        >
          <div className="font-luckiest text-sm sm:text-base">
            {playing
              ? playing === "tuh"
                ? "now playing — dat bih TUH"
                : "now playing — dat bih GAH"
              : "tap a button to hear it."}
          </div>
          <span
            className="inline-flex items-center gap-2 font-luckiest text-xs"
          >
            <span
              className="inline-block w-3 h-3 rounded-full"
              style={{
                background: playing ? "#0057FF" : "#000",
                animation: playing ? "blink 0.7s ease-in-out infinite" : "none",
              }}
            />
            {playing ? "LIVE" : "IDLE"}
          </span>
        </div>
      </div>
    </section>
  );
};

const SoundButton = ({
  label,
  sub,
  color,
  textColor = "#000",
  testId,
  onClick,
  active,
  pulsing,
}) => {
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onClick}
      className={`relative sticker p-6 sm:p-8 text-left overflow-hidden transition-transform ${
        pulsing ? "scale-[1.02]" : ""
      }`}
      style={{
        background: color,
        color: textColor,
        boxShadow: active ? "10px 10px 0 #000" : "8px 8px 0 #000",
        cursor: "pointer",
        WebkitTapHighlightColor: "transparent",
      }}
      aria-label={`play ${label}`}
    >
      <div className="flex items-center justify-between gap-4">
        <div>
          <div
            className="font-bangers leading-none"
            style={{
              fontSize: "clamp(40px, 6vw, 76px)",
              WebkitTextStroke: "2px #000",
              color: textColor === "#fff" ? "#fff" : "#000",
            }}
          >
            {label}
          </div>
          <div
            className="font-luckiest text-sm sm:text-base mt-2 opacity-80"
            style={{ color: textColor }}
          >
            {sub}
          </div>
        </div>
        <div
          className="shrink-0 w-14 h-14 sm:w-20 sm:h-20 border-[3px] border-black rounded-full flex items-center justify-center"
          style={{ background: textColor === "#fff" ? "#000" : "#fff" }}
        >
          <PlayIcon color={textColor === "#fff" ? "#fff" : "#000"} />
        </div>
      </div>
      {active && (
        <div className="absolute inset-x-0 bottom-0 flex items-end justify-center gap-1 px-6 pb-3 pointer-events-none">
          {[0, 1, 2, 3, 4, 5, 6].map((i) => (
            <span
              key={i}
              className="block w-1.5 sm:w-2 rounded-sm"
              style={{
                background: textColor === "#fff" ? "#fff" : "#000",
                height: `${10 + ((i * 13) % 28)}px`,
                animation: `eq 0.6s ease-in-out ${i * 0.07}s infinite alternate`,
              }}
            />
          ))}
        </div>
      )}
    </button>
  );
};

const PlayIcon = ({ color = "#000" }) => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill={color} aria-hidden="true">
    <path d="M7 5v14l12-7z" />
  </svg>
);
